/* A50-VIDEO-HIGHLIGHTS — custom Authenticity50 feature (isolated file).
 *
 * Owns playback for the <a50-video-highlights> element. Vanilla, ES-module
 * compatible, no dependencies, no imports from the theme bundle.
 *
 * ── Why this exists rather than reusing Impact's <video-media> ──────────────
 * <video-media> starts a video on first intersection and NEVER pauses it on
 * exit (assets/theme.js, BaseMedia#connectedCallback — `inView(this, this.play)`
 * with no exit handler). In a four-up grid that means every video the visitor
 * scrolls past keeps decoding for the rest of the page. It also ships its own
 * autoplay logic, so running it underneath a controller that starts and stops
 * players yields double-starts and two owners disagreeing about state.
 *
 * ── State model: observe, never infer ──────────────────────────────────────
 *   • Per-card state lives on a card record, not on the section. One shared
 *     flag once let pausing a single card disable autoplay for all of them.
 *   • The <video> element's own `play`/`pause` events are the ONLY writer of
 *     playing/paused state, so native controls, the custom button and the
 *     controller all converge — and there is no promise race to lose.
 *   • Visibility comes from the IntersectionObserver's ratio, which is correct
 *     on both axes and reads the same in every engine (see _observe). Never
 *     recompute it from geometry.
 *   • `_allPaused` is touched by the pause-all button alone.
 *   • Once a visitor turns a card's sound on, the controller never starts that
 *     card again and never mutes it. It still pauses it on scroll-out.
 *   • A pause while the page is hidden is never the visitor's. Muted cards are
 *     paused by the controller on hide and resumed on return if still eligible.
 *
 * ── Progressive enhancement ────────────────────────────────────────────────
 * Nothing here is required for the section to be useful. With this file blocked
 * or broken the markup still renders posters, headings, body copy and links;
 * the play buttons simply stay [hidden], which is why Liquid renders them
 * hidden rather than having JS create them. Nothing in this file touches
 * add-to-cart, checkout or navigation.
 *
 * ── No user-facing strings live here ───────────────────────────────────────
 * Every label is passed in from Liquid via data attributes, where it resolves
 * from a merchant setting or a locale key. There is deliberately no English
 * fallback in this file.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/* How much of a card must be on screen before it counts as visible. */
const VISIBLE_RATIO = 0.25;

/* Audible, as the visitor set it. `muted` alone is not enough: dragging the
 * volume to zero is silent without being muted. iOS reports a fixed volume of 1,
 * so there it comes down to `muted`, which is right. */
const SOUND_ON = (video) => !video.muted && video.volume > 0;

/* Videos the CONTROLLER paused, awaiting their `pause` event.
 *
 * Keyed on the <video> element rather than held on the card record, because the
 * record does not survive a disconnect/reconnect and the marker has to. The
 * sequence that forced this: disconnectedCallback aborts the listeners, THEN
 * pauses each video; `pause` fires asynchronously, so the event can arrive after
 * the element is reconnected and rebuilt with fresh records — and the new
 * listener would read it as the visitor pausing, permanently suppressing
 * autoplay for that card. A WeakSet on the element itself spans that gap and
 * needs no cleanup. */
const controllerPaused = new WeakSet();

class A50VideoHighlights extends HTMLElement {
  connectedCallback() {
    // Guard against a double upgrade (custom element registered twice, or the
    // editor re-inserting a live node rather than re-rendering it).
    if (this._initialised) return;
    this._initialised = true;

    this._abortController = new AbortController();
    this._signal = this._abortController.signal;
    this._allPaused = false;

    this._reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    this._onReducedMotionChange = this._handleReducedMotionChange.bind(this);
    // Safari < 14 only has the deprecated addListener.
    if (typeof this._reducedMotion.addEventListener === 'function') {
      this._reducedMotion.addEventListener('change', this._onReducedMotionChange);
    } else if (typeof this._reducedMotion.addListener === 'function') {
      this._reducedMotion.addListener(this._onReducedMotionChange);
    }

    this._cards = Array.from(this.querySelectorAll('.a50-video-highlights__media'))
      .map((frame) => ({ frame, video: frame.querySelector('video') }))
      .filter(({ video }) => video)
      .map(({ frame, video }) => ({
        frame,
        video,
        button: frame.querySelector('[data-a50-vh-toggle]'),
        // Set by the IntersectionObserver. Correct on both axes, which matters
        // in horizontal-scroll mode where cards share a vertical band.
        visible: false,
        // The visitor stopped THIS card and means it to stay stopped.
        userPaused: false,
        // The visitor turned THIS card's sound on (native controls only). Read
        // from the element rather than assumed false: a live node re-inserted by
        // the editor keeps its <video>, and the visitor's volume, with it.
        // Afterwards it is written by the `volumechange` handler alone.
        soundOn: SOUND_ON(video)
      }));

    if (this._cards.length === 0) return;

    this._nameVideos();
    this._bindMediaEvents();
    this._revealControls();
    this._bindToggles();
    this._bindPauseAll();
    this._bindPageVisibility();
    this._observe();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._observer?.disconnect();

    if (typeof this._reducedMotion?.removeEventListener === 'function') {
      this._reducedMotion.removeEventListener('change', this._onReducedMotionChange);
    } else if (typeof this._reducedMotion?.removeListener === 'function') {
      this._reducedMotion.removeListener(this._onReducedMotionChange);
    }

    // Leave nothing decoding behind us when the editor swaps the section out.
    this._cards?.forEach((card) => this._pause(card, { byController: true }));
    this._initialised = false;
  }

  get autoplayRequested() {
    return this.hasAttribute('autoplay');
  }

  /* Whether THIS card may start on its own right now. Per-card intent and
   * section-wide intent are separate inputs and must stay that way.
   *
   * A card with its sound on is never started automatically. Restarting it on
   * scroll-in would either start audio the visitor did not ask for at that
   * moment or, worse, have to mute it and throw their choice away. It waits
   * for them to press play. */
  _shouldAutoplay(card) {
    return (
      this.autoplayRequested &&
      !this._reducedMotion.matches &&
      !this._allPaused &&
      !card.userPaused &&
      !card.soundOn
    );
  }

  /* ── The single source of truth ────────────────────────────────────────────
   * The <video> element tells us what it is doing. Native controls, the custom
   * button, the observer and a late-resolving play() promise all funnel through
   * these two handlers, so the rendered state can never disagree with reality. */
  _bindMediaEvents() {
    this._cards.forEach((card) => {
      // Both handlers bail on an event that no longer describes reality.
      //
      // `play()` flips `paused` synchronously but fires `play` as a queued task,
      // so a fast scroll can run play() and pause() back to back — an
      // IntersectionObserver callback can carry enter AND exit for one target —
      // and the handlers then run against a video that has already settled the
      // other way. Without these guards the stale `play` deleted the marker that
      // the real `pause` was about to consume, the pause was recorded as the
      // visitor's, and the card never autoplayed again.
      card.video.addEventListener(
        'play',
        () => {
          if (card.video.paused) return;

          card.userPaused = false;
          // Clear any marker left behind by a pause whose event fired while no
          // listener was attached — otherwise it would be consumed by some
          // unrelated pause much later.
          controllerPaused.delete(card.video);
          card.frame.dataset.a50VhState = 'playing';
          this._syncToggle(card);
        },
        { signal: this._signal }
      );

      card.video.addEventListener(
        'pause',
        () => {
          if (!card.video.paused) return;

          card.frame.dataset.a50VhState = 'paused';

          if (controllerPaused.has(card.video) || document.hidden) {
            // Ours — scrolled out of view, pause-all, page hidden, or teardown.
            // A pause while the page is hidden is never the visitor's either:
            // it is the browser suspending media (a phone switching apps).
            // Not a visitor decision, so the card stays eligible to autoplay.
            controllerPaused.delete(card.video);
          } else {
            // Theirs — the custom button or the native controls. Must stick,
            // or the observer would restart it on the next scroll.
            card.userPaused = true;
          }

          this._syncToggle(card);
        },
        { signal: this._signal }
      );

      // Only native controls can change the volume; this section never unmutes.
      card.video.addEventListener(
        'volumechange',
        () => {
          card.soundOn = SOUND_ON(card.video);
        },
        { signal: this._signal }
      );
    });
  }

  /* Give each <video> the accessible name Liquid resolved: the card's
   * Accessible name, else the video file's alt text, else the heading.
   *
   * Liquid cannot do this reliably: `aria_label` is not a documented video_tag
   * parameter. video_tag names the player from the file's alt text by itself,
   * so this REPLACES that name — with the same text when the file's alt is what
   * Liquid picked, and otherwise with the merchant's explicit name or, when the
   * file has no alt, the heading. It matters most with native controls, where
   * this is the player's only name.
   *
   * With no resolved name the attribute is left alone. */
  _nameVideos() {
    this._cards.forEach((card) => {
      const label = card.frame.dataset.a50VhLabel;
      if (label) card.video.setAttribute('aria-label', label);
    });
  }

  /* The controls exist in the markup but start hidden, so that a failed script
   * leaves no dead buttons behind. Reaching this line is the proof they work. */
  _revealControls() {
    this._cards.forEach((card) => {
      if (card.button) {
        card.button.hidden = false;
        this._syncToggle(card);
      }
    });

    const pauseAll = this._pauseAllButton();
    if (pauseAll && this.autoplayRequested && !this._reducedMotion.matches) {
      pauseAll.hidden = false;
    }
  }

  _pauseAllButton() {
    // The button is a sibling of this element, not a child — it sits outside the
    // scroll container so it never scrolls away from the videos it controls.
    return this.parentElement?.querySelector('[data-a50-vh-pause-all]') ?? null;
  }

  _isPlaying(card) {
    return card.frame.dataset.a50VhState === 'playing';
  }

  _bindToggles() {
    this._cards.forEach((card) => {
      if (!card.button) return;

      card.button.addEventListener(
        'click',
        () => {
          if (this._isPlaying(card)) {
            // byController is false, so the `pause` handler records the intent.
            this._pause(card);
          } else {
            // Clears THIS card's intent only. `_allPaused` is deliberately not
            // touched: "Pause videos" is a section-wide request to stop the
            // motion — plausibly an accessibility one — and playing a single
            // card must not silently re-arm autoplay for all the others. It also
            // keeps this path identical to playing via native controls, which
            // has no way to reach `_allPaused` either.
            //
            // Consequence, accepted: while the section is paused the button
            // still reads "Play videos" even though one card is running. It
            // means "resume all", which is still true.
            card.userPaused = false;
            this._play(card);
          }
        },
        { signal: this._signal }
      );
    });
  }

  _bindPauseAll() {
    const button = this._pauseAllButton();
    if (!button) return;

    button.addEventListener(
      'click',
      () => {
        this._allPaused = !this._allPaused;

        if (this._allPaused) {
          this._cards.forEach((card) => this._pause(card, { byController: true }));
        } else {
          // An explicit section-wide "play" overrides earlier per-card pauses;
          // otherwise the button would appear to do nothing. A card with its
          // sound on is still left for the visitor to restart themselves.
          this._cards.forEach((card) => {
            card.userPaused = false;
          });
          this._cards
            .filter((card) => card.visible && this._shouldAutoplay(card))
            .forEach((card) => this._play(card));
        }

        this._syncPauseAllLabel();
      },
      { signal: this._signal }
    );

    this._syncPauseAllLabel();
  }

  /* Labels come from Liquid. The name changes with state rather than carrying
   * aria-pressed as well — a control should convey its state one way, not two. */
  _syncPauseAllLabel() {
    const button = this._pauseAllButton();
    if (!button) return;

    const label = button.querySelector('[data-a50-vh-pause-label]');
    const next = this._allPaused ? button.dataset.playLabel : button.dataset.pauseLabel;

    if (label && next) label.textContent = next;
  }

  _syncToggle(card) {
    if (!card.button) return;

    const label = card.button.querySelector('[data-a50-vh-toggle-label]');
    const playing = this._isPlaying(card);
    const next = playing ? card.button.dataset.pauseLabel : card.button.dataset.playLabel;

    if (label && next) label.textContent = next;
  }

  /* The page is hidden (another tab, or a phone switching apps) and shown again.
   *
   * On hide the controller pauses every muted card itself instead of leaving
   * it to the browser. A browser's own pause may not be delivered until the
   * visitor returns, and by then `document.hidden` is false, so it would read
   * as the visitor's pause and stop that card autoplaying for good. Ours is
   * marked, so it is recorded correctly whenever its event lands. It also stops
   * a background tab decoding video nobody can see.
   *
   * A card with its sound on is left alone: on desktop the visitor may be
   * listening from another tab. If the browser pauses it anyway, the
   * `document.hidden` check in the `pause` handler covers that case.
   *
   * On return, only what `_shouldAutoplay` allows resumes, and only on screen. */
  _bindPageVisibility() {
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this._cards
            .filter((card) => !card.soundOn)
            .forEach((card) => this._pause(card, { byController: true }));
          return;
        }

        this._cards
          .filter((card) => card.visible && this._shouldAutoplay(card))
          .forEach((card) => this._play(card));
      },
      { signal: this._signal }
    );
  }

  _observe() {
    if (!('IntersectionObserver' in window)) {
      // No observer: play nothing automatically rather than play everything.
      // The per-card buttons still work, which is the safe degradation.
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = this._cards.find((item) => item.frame === entry.target);
          if (!card) return;

          // The ratio decides, not `isIntersecting` alone. Chrome only reports
          // isIntersecting once the threshold is met, but the spec — and so
          // other engines — report it for ANY overlap. Trusting it there would
          // start a card on its first visible pixel, and on the way out treat
          // the 25% crossing as an entry: restart instead of pause.
          //
          // Compare against exactly the observer's own threshold, with no
          // margin. The browser judges the crossing by the same ratio it
          // reports, so an upward crossing is always >= VISIBLE_RATIO and a
          // downward one always below it. A margin under the threshold would
          // read a slow downward crossing (~0.249) as still visible, and no
          // further entry arrives until the card is fully off screen.
          card.visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;

          if (card.visible) {
            if (this._shouldAutoplay(card)) this._play(card);
          } else {
            // The pause Impact's <video-media> never does. Without it a long
            // page ends up with every card in this section still decoding.
            // Applies to a card with its sound on too; `_shouldAutoplay` is
            // what keeps it from restarting.
            this._pause(card, { byController: true });
          }
        });
      },
      { rootMargin: '0px', threshold: VISIBLE_RATIO }
    );

    this._cards.forEach((card) => this._observer.observe(card.frame));
  }

  _handleReducedMotionChange() {
    const pauseAll = this._pauseAllButton();

    if (this._reducedMotion.matches) {
      this._cards.forEach((card) => this._pause(card, { byController: true }));
      if (pauseAll) pauseAll.hidden = true;
      return;
    }

    if (pauseAll && this.autoplayRequested) pauseAll.hidden = false;

    // Visibility is the observer's, so this starts only what is really on
    // screen — including horizontally, which geometry checks used to miss.
    this._cards
      .filter((card) => card.visible && this._shouldAutoplay(card))
      .forEach((card) => this._play(card));
  }

  _play(card) {
    const { video } = card;
    // Already running: there is nothing to start, and running the muting below
    // on a playing card would silence a visitor who just turned it up.
    if (!video || !video.paused) return;

    // preload="none" in the markup keeps a grid of videos off the critical
    // path; the first play is where we actually want the bytes.
    if (video.preload === 'none') video.preload = 'metadata';

    // Autoplay is only ever eligible when muted, everywhere. This section never
    // unmutes on its own — a grid of cards that suddenly makes noise is worse
    // than one that stays quiet — and never re-mutes one the visitor turned up.
    if (!card.soundOn) video.muted = true;

    const attempt = video.play();

    // State is written by the `play` / `pause` handlers, never here — that is
    // what removes the race between a slow play() and an intervening pause().
    // Swallow the rejection so a blocked autoplay is not an unhandled promise.
    // Nothing else is needed: no `play` event fires, so the card stays in its
    // paused state, which already shows the video's own poster and leaves the
    // retry control visible. Native controls are deliberately NOT switched on
    // here — the full-frame custom toggle sits over the video and would make
    // them unclickable.
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {});
    }
  }

  _pause(card, { byController = false } = {}) {
    const { video } = card;
    if (!video || video.paused) return;

    // Consumed by the `pause` handler. Only marked when a pause event will
    // actually fire, so it cannot leak onto a later visitor-initiated pause.
    if (byController) controllerPaused.add(video);
    video.pause();
  }

  /* Public, so the theme-editor hook below does not have to reach into
   * internals it should not know about. */
  pauseAll() {
    this._cards?.forEach((card) => this._pause(card, { byController: true }));
  }
}

if (!window.customElements.get('a50-video-highlights')) {
  window.customElements.define('a50-video-highlights', A50VideoHighlights);
}

/* Theme editor lifecycle. Custom element callbacks already cover load and
 * unload — Shopify replaces the section's DOM, which fires disconnected then
 * connected. `shopify:section:select` is the one that needs handling: the
 * merchant has just clicked this section in the editor, and autoplaying video
 * while they are trying to read settings is a distraction, so pause instead. */
document.addEventListener('shopify:section:select', (event) => {
  const element = event.target?.querySelector?.('a50-video-highlights');
  if (typeof element?.pauseAll === 'function') element.pauseAll();
});
